import { IDataPackage, IDataPackageConfigProps } from '@vivli/shared/features/data-package/infrastructure/interface';
import { DataPackageLoaderComponent } from '../data-package-loader.component';
import { useDataPackage } from '@vivli/shared/infrastructure/hook';
import { DataPackageContext } from '@vivli/shared/features/data-package/infrastructure/context';
import { DataPackageV2CoreComponent } from './data-package-v2-core.component';
import { FileUploadWarningChatComponent } from '../file-upload-warning-chat.component';
import { FileUploadWarningComponent } from '../file-upload-warning.component';
import { useModalApiContext } from '@vivli/core/infrastructure/context';

interface DataPackageV2ComponentProps extends IDataPackageConfigProps {
    dataPackageId: string; //data package id for study IPD package
    secondaryDataPackageId?: string;
    onDataPackageLoaded?: (dataPackage: IDataPackage) => void;
    hideForPubliclyAvailableTypes?: string[];
}

export const DataPackageV2Component = ({
    dataPackageId,
    secondaryDataPackageId,
    onDataPackageLoaded,
    ...props
}: DataPackageV2ComponentProps) => {
    const hybridMode = !!secondaryDataPackageId;
    const _modalService = useModalApiContext();

    const handleFileExtensionWarning = () => {
        return new Promise<boolean>((resolve, reject) => {
            const modalId = _modalService.custom(
                props.showChatUploadFileWarning ? (
                    <FileUploadWarningChatComponent
                        handleConfirm={() => {
                            _modalService.dismiss(modalId);
                            resolve(true);
                        }}
                        handleCancel={() => {
                            _modalService.dismiss(modalId);
                            reject(false);
                        }}
                    />
                ) : (
                    <FileUploadWarningComponent
                        handleConfirm={() => {
                            _modalService.dismiss(modalId);
                            resolve(true);
                        }}
                        handleCancel={() => {
                            _modalService.dismiss(modalId);
                            reject(false);
                        }}
                    />
                )
            );
        });
    };

    const dataPackageProps = useDataPackage({
        dataPackageId,
        secondaryDataPackageId,
        onDataPackageReady: onDataPackageLoaded,
        ...props,
        onFileExtensionWarning: handleFileExtensionWarning
    });

    if (!dataPackageProps.dataPackage) {
        return <DataPackageLoaderComponent />;
    }

    //allowUpload/allowDownload accept either a boolean or a predicate evaluated against the loaded
    //package. Resolve them here - the only point where the package is guaranteed loaded - so every
    //consumer downstream gets a real boolean. A predicate left unresolved is always truthy. (DEV-488)
    const resolveFlag = (flag: boolean | ((dataPackage: IDataPackage) => boolean)) =>
        typeof flag === 'function' ? flag(dataPackageProps.dataPackage) : !!flag;

    return (
        <DataPackageContext.Provider
            value={{
                ...props,
                ...dataPackageProps,
                allowUpload: resolveFlag(props.allowUpload),
                allowDownload: resolveFlag(props.allowDownload),
                hybridMode
            }}
        >
            <DataPackageV2CoreComponent />
        </DataPackageContext.Provider>
    );
};
